import { useState, useEffect } from "react";
import { Button } from "../ui/button";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "../ui/dialog";
import { AlertTriangle } from "lucide-react";

/**
 * HubSpot-style type-to-confirm destructive action dialog.
 *
 * @param {boolean} open - Whether the dialog is open
 * @param {function} onOpenChange - Callback when open state changes
 * @param {string} title - Dialog title (e.g. "Delete Course")
 * @param {string} description - What will happen (e.g. "This will permanently delete the course and all enrollments.")
 * @param {string} confirmWord - Word to type to confirm (default: "DELETE")
 * @param {function} onConfirm - Called when user confirms
 * @param {string} confirmLabel - Button label (default: "Yes, Delete")
 */
export default function ConfirmDeleteDialog({
  open, onOpenChange, title, description, confirmWord = "DELETE",
  onConfirm, confirmLabel = "Yes, Delete",
}) {
  const [text, setText] = useState("");

  useEffect(() => { if (!open) setText(""); }, [open]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-red-700">
            <AlertTriangle className="w-5 h-5" /> {title}
          </DialogTitle>
        </DialogHeader>
        <div className="py-3 space-y-4">
          <p className="text-sm text-slate-700">{description}</p>
          <div className="p-3 bg-red-50 border border-red-200 rounded-lg">
            <p className="text-xs text-red-700 font-medium">This action cannot be undone.</p>
          </div>
          <div className="space-y-2">
            <label className="text-sm font-medium text-slate-700">
              Type <span className="font-bold text-red-700 bg-red-50 px-1.5 py-0.5 rounded text-xs tracking-wide">{confirmWord}</span> to confirm
            </label>
            <input
              type="text"
              value={text}
              onChange={(e) => setText(e.target.value)}
              placeholder={`Type ${confirmWord} here`}
              className="w-full h-10 px-3 rounded-lg border-2 border-slate-200 text-sm font-medium tracking-wide focus:outline-none focus:border-red-400 focus:ring-2 focus:ring-red-100 transition-colors"
              autoComplete="off"
              spellCheck="false"
              data-testid="confirm-text-input"
            />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button variant="destructive" onClick={() => { onConfirm(); onOpenChange(false); }} disabled={text !== confirmWord} data-testid="confirm-delete-btn">{confirmLabel}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
